import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Settings, 
  ArrowLeft, 
  Key, 
  User,
  Mail,
  Loader2,
  CheckCircle,
  AlertCircle,
  Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminSettings() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [senhaError, setSenhaError] = useState('');
  const [senhaSuccess, setSenhaSuccess] = useState('');
  const [senhaLoading, setSenhaLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');

    if (!token) {
      navigate('/admin');
      return;
    }

    if (adminUser) {
      setAdmin(JSON.parse(adminUser));
    }
  }, [navigate]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSenhaError('');
    setSenhaSuccess('');

    if (novaSenha !== confirmarSenha) {
      setSenhaError('As senhas não coincidem');
      return;
    }

    if (novaSenha.length < 6) {
      setSenhaError('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }

    setSenhaLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin-panel/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          senha_atual: senhaAtual,
          nova_senha: novaSenha
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao alterar senha');
      }

      setSenhaSuccess('Senha alterada com sucesso!');
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
    } catch (err) {
      setSenhaError(err.message);
    } finally {
      setSenhaLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-settings">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-500 hover:text-blue-600"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-slate-500" />
                <h1 className="text-lg font-semibold text-slate-800">Configurações</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Informações do Admin */}
        <Card className="bg-white border-slate-200 mb-6 shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Informações da Conta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Usuário</Label>
                <p className="text-slate-800 mt-1">{admin?.username || '-'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Nome</Label>
                <p className="text-slate-800 mt-1">{admin?.nome || '-'}</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-500">Email</Label>
                <p className="text-slate-800 mt-1 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  {admin?.email || '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alterar Senha */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Alterar Senha
            </CardTitle>
            <CardDescription className="text-slate-500">
              Altere sua senha de acesso ao painel administrativo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {senhaError && (
                <Alert variant="destructive" className="bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">{senhaError}</AlertDescription>
                </Alert>
              )}

              {senhaSuccess && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">{senhaSuccess}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="senhaAtual" className="text-slate-700">Senha Atual</Label>
                <Input
                  id="senhaAtual"
                  type="password"
                  value={senhaAtual}
                  onChange={(e) => setSenhaAtual(e.target.value)}
                  placeholder="Digite sua senha atual"
                  className="bg-white border-slate-300"
                  disabled={senhaLoading}
                  required
                  data-testid="current-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="novaSenha" className="text-slate-700">Nova Senha</Label>
                <Input
                  id="novaSenha"
                  type="password"
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                  className="bg-white border-slate-300"
                  disabled={senhaLoading}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmarSenha" className="text-slate-700">Confirmar Nova Senha</Label>
                <Input
                  id="confirmarSenha"
                  type="password"
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  placeholder="Confirme a nova senha"
                  className="bg-white border-slate-300"
                  disabled={senhaLoading}
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={senhaLoading}
                data-testid="change-password-button"
              >
                {senhaLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Alterando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Alterar Senha
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
